/**
 * ONE review item, composed by both the inline reviews block and the full
 * reviews list. Rebuilt to the reference's order after measuring
 * `shop.app/m/johnelliott` at 414 and 1440 (computed styles, not eyeballed):
 *
 *   ┌ card — radius 20, hairline border, translucent surface, padding 16
 *   │  ▸ HEAD (a link to the product)
 *   │      64×64 thumbnail of the ITEM being reviewed, radius 12
 *   │      the RATING (five stars, 20px, gap 2)
 *   │      the item's NAME — "Product / Variant", 12px, muted
 *   │  ▸ the DESCRIPTION, 14px
 *   │  ▸ the photo strip, when the buyer added photos
 *   │  ▸ FOOTER — 24px reviewer avatar · "Name · time" · "Helpful"
 *   └
 *
 * Two things that are easy to get wrong and are load-bearing here:
 *
 * 1. **The head is the link, not the whole card.** Item 10 of the backlog is
 *    "review items are clickable and open the product page", and the obvious
 *    reading — wrap everything — swallows the Helpful button and the photo
 *    thumbnails inside an anchor, which is both invalid and unusable. The
 *    reference wraps exactly the thumbnail; we widen that to the whole head
 *    row (thumb + stars + name) so the target is real on a phone, and leave
 *    the interactive tail outside it.
 * 2. **No colour is written here.** Every value in `reviewCard.module.css`
 *    resolves from `--shop-*`, i.e. from the page background the owner chose
 *    (FULL / LIGHT / CLASSIC), with the pre-tint product palette as the
 *    fallback. A gold star and a white card look fine on the one page the
 *    author happened to have open; that is how this surface got here.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { reviewerDisplayName, reviewPhotos, variantLabel } from '../../utils/reviewsDisplay';
import { formatReviewTime } from './reviewTime';
import ReviewReply from './ReviewReply';
import styles from './reviewCard.module.css';

/**
 * The five-star strip. Filled stars take the page foreground and empty ones
 * a measured dilution of it — the reference does exactly this, and it is the
 * only version that survives all three background modes: a fixed pale grey
 * for "empty" disappears on a light page and a fixed gold disappears on a
 * pale one.
 *
 * The strip carries the rating as text for assistive tech; the glyphs
 * themselves are decorative.
 */
/* `count` draws fewer than five glyphs — the histogram's row label is
 * "5 ★", one star beside a number, not a second five-star strip. */
export const StarsRow = ({ value, size = 20, t, testId, count = 5 }) => {
  const rounded = Math.round(Number(value) || 0);
  return (
    <span
      className={styles.stars}
      style={{ '--rv-star-size': `${size}px` }}
      role="img"
      aria-label={t
        ? t('reviews.starsAria', { defaultValue: '{{n}} out of 5', n: rounded })
        : `${rounded} out of 5`}
      data-testid={testId}
    >
      {Array.from({ length: count }, (_, i) => i + 1).map((s) => (
        <span key={s} className={s <= rounded ? styles.starOn : styles.starOff} aria-hidden />
      ))}
    </span>
  );
};

/** "Product name / Variant" — the reference's caption under the stars. */
export function itemLabel(product, review) {
  const name = (product?.name || '').trim();
  const variant = variantLabel(review);
  if (name && variant) return `${name} / ${variant}`;
  return name || variant || '';
}

const ReviewCard = ({
  review,
  product,
  productHref,
  onOpenProduct,
  onHelpful,
  helpfulBusy = false,
  onPhoto,
  t,
  locale,
  shopName,
  shopLogo,
  maxPhotos = 5,
  testIdPrefix = 'review-card',
}) => {
  if (!review) return null;

  const photos = reviewPhotos(review).slice(0, maxPhotos);
  const masked = reviewerDisplayName(review.customer?.fullName);
  const avatarUrl = review.customer?.photoUrl || review.customer?.avatarUrl || null;
  const initial = (masked || '').trim().charAt(0) || '·';
  const when = formatReviewTime(review.createdAt, locale);
  const label = itemLabel(product, review);
  const thumb = product?.imageUrl || null;
  const helpfulCount = Number(review.helpfulCount) || 0;

  const head = (
    <>
      <span className={styles.thumb}>
        {thumb ? (
          <img
            src={thumb}
            alt=""
            className={styles.thumbImg}
            loading="lazy"
            data-testid={`${testIdPrefix}-item-image`}
          />
        ) : (
          <span className={styles.thumbFallback} aria-hidden data-testid={`${testIdPrefix}-item-image`}>
            {(label || '·').trim().charAt(0)}
          </span>
        )}
      </span>
      <span className={styles.headText}>
        <StarsRow value={review.stars} t={t} testId={`${testIdPrefix}-stars`} />
        {label && (
          <span className={styles.itemName} data-testid={`${testIdPrefix}-item-name`}>{label}</span>
        )}
      </span>
    </>
  );

  return (
    <article
      className={styles.card}
      data-review-id={review.id}
      data-testid={`${testIdPrefix}-${review.id}`}
    >
      {productHref ? (
        <Link
          to={productHref}
          className={styles.head}
          onClick={() => onOpenProduct?.(product?.id ?? review.productId)}
          aria-label={t('reviews.openProduct', {
            defaultValue: 'Open {{name}}',
            name: label || t('reviews.thisProduct', { defaultValue: 'this product' }),
          })}
          data-testid={`${testIdPrefix}-item-link`}
        >
          {head}
        </Link>
      ) : (
        <div className={styles.head}>{head}</div>
      )}

      {review.body && (
        <p className={styles.body} data-testid={`${testIdPrefix}-body`}>{review.body}</p>
      )}

      {photos.length > 0 && (
        <div className={styles.photoStrip} data-testid={`${testIdPrefix}-photos`}>
          {photos.map((url, i) => (
            <button
              key={url || i}
              type="button"
              className={styles.photoBtn}
              onClick={() => onPhoto?.(photos, i)}
              aria-label={t('reviews.openPhoto', { defaultValue: 'Open photo' })}
            >
              <img src={url} alt="" className={styles.photoImg} loading="lazy" />
            </button>
          ))}
        </div>
      )}

      <div className={styles.footer}>
        <span className={styles.avatar} data-testid={`${testIdPrefix}-avatar`}>
          {avatarUrl
            ? <img src={avatarUrl} alt="" className={styles.avatarImg} loading="lazy" data-testid={`${testIdPrefix}-avatar-image`} />
            : <span aria-hidden>{initial}</span>}
        </span>
        {/* `<bdi>` around each half, not one interpolated string.
          * "ندى · 2 days ago" written as a single run puts the separator
          * under the bidi algorithm's control and it lands on the wrong side
          * of the number — rendered as "ن***ب 2 · days ago" on the live page
          * before this. Isolation is the fix, and it is needed in BOTH
          * directions: an English name beside an Arabic date breaks the same
          * way in an Arabic UI. */}
        <span className={styles.byline} data-testid={`${testIdPrefix}-byline`}>
          <bdi>{masked}</bdi>
          {when && <span className={styles.bylineDot} aria-hidden>·</span>}
          {when && <bdi>{when}</bdi>}
        </span>
        <button
          type="button"
          className={`${styles.helpful} ${review.helpfulByMe ? styles.helpfulOn : ''}`}
          onClick={() => onHelpful?.(review.id)}
          disabled={helpfulBusy}
          aria-pressed={!!review.helpfulByMe}
          data-testid={`${testIdPrefix}-helpful-${review.id}`}
        >
          <span className={styles.thumbIcon} aria-hidden />
          <span className={styles.helpfulLabel}>
            {t('reviews.helpful', { defaultValue: 'Helpful' })}
            {helpfulCount > 0 ? ` · ${helpfulCount}` : ''}
          </span>
        </button>
      </div>

      <ReviewReply reply={review.reply} shopName={shopName} shopLogo={shopLogo} t={t} />
    </article>
  );
};

export default ReviewCard;
